import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import CompensacoesModal from './components/modals/CompensacaoModal';
import usePagination10 from './components/hooks/usePagination10';
import useReferenceData from './components/hooks/useReferenceData';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import { formatCurrency } from './utils/formatMoeda';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = { ano: '', idPrograma: '', idEscola: '', idConta: '', vlrC: '', vlrK: '' };

function Compensacoes() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const isGestor = JSON.parse(localStorage.getItem('vinculo') || '{}')?.tipo === 'GEST';
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '', ano: ano || '' });
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const { programas, contas } = useReferenceData(token, cnpj);
  const displayed = items.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  useEffect(() => {
    if (!cnpj || !ano) { showDefaultModal('error', 'Escola ou ano não selecionado.'); setIsLoading(false); return; }
    setIsLoading(true);
    axios.get(`${API_BASE_URL.COMPENSACAO}/all`, {
      params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        setItems(r.data);
      })
      .catch(err => {
        if (err.response && err.response.status !== 200 && err.response.status !== 404) {
          showDefaultModal('error', 'Erro ao carregar compensações.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano, atualizar]);

  function handleChange(e) { const { name, value } = e.target; setForm(f => ({ ...f, [name]: value })); }
  function handleNew() { setForm({ ...emptyForm, idEscola: cnpj || '', ano: ano || '', _isEditing: false }); setShowModal(true); }
  function handleEdit() { if (selected.length !== 1) return; setForm({ ...selected[0], _isEditing: true }); setShowModal(true); }

  function handleSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.COMPENSACAO}/save`, form, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]); // Limpa seleção após salvar
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar compensação.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s => axios.post(`${API_BASE_URL.COMPENSACAO}/delete`, {
      ano: s.ano, idPrograma: s.idPrograma, idEscola: s.idEscola, idConta: s.idConta
    }, { headers: { Authorization: `Bearer ${token}` } })))
      .then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir compensação.'))
    );
  }

  function keyOf(item) { return `${item.ano}_${item.idPrograma}_${item.idEscola}_${item.idConta}`; }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="COMPENSAÇÕES" icon="bi-balance-scale" />
        <PageContent>
          <Toolbar
            onNew={!isGestor ? handleNew : undefined}
            onEdit={!isGestor ? handleEdit : undefined} disableEdit={selected.length !== 1}
            onDelete={!isGestor ? handleDelete : undefined} disableDelete={selected.length === 0}
          />
          <TableContainer isLoading={false}>
                              <DataTable head={<tr>
                  <th style={{ width: '3rem' }}>#</th><th>Programa</th><th>Conta</th><th style={{ width: '6rem' }}>Valor Custeio</th><th style={{ width: '6rem' }}>Valor Capital</th><th style={{ width: '7rem' }}>Valor Total</th>
                </tr>}>
                <tbody>
                  {displayed.map((item, idx) => (
                    <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                      <td>{setPagination.startIndex + idx + 1}</td><td>{programas[item.idPrograma]?.descricao || item.idPrograma}</td><td>{item.idConta}</td><td className="text-end">{formatCurrency(item.vlrC)}</td><td className="text-end">{formatCurrency(item.vlrK)}</td>
                      <td className="align-middle text-end fw-bold text-success" style={{ fontSize: '1rem' }}>{formatCurrency(parseFloat(item.vlrC || 0) + parseFloat(item.vlrK || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <CompensacoesModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} programas={programas} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Compensacoes;
